import React, { Fragment, useState, useEffect } from "react";
import { useAllRoles, useAddUser, useEditUser } from "hooks/user/user";
import { Button, Row, Col, Form, Input, Upload, Select, message } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { profile_image_type, strongPasswordRule } from "helpers/commonFunctions";
import PrivateImage from 'helpers/PrivateImage';
import Image from 'helpers/Image';
import "../style.css";

const { Option } = Select;

const FormAdmin = () => {

    let navigate = useNavigate();

    const [form] = Form.useForm();
    const editData = useLocation();
    const [serverErrors, setServerErrors] = useState({});   
    const create = editData.pathname === "/admin/create";

    const [files, setFiles] = useState(null);
    const [showPassword, setPassword] = useState(false);

    const { data: userRoleData } = useAllRoles()
    const { mutate: addUser, isLoading: Loading } = useAddUser();
    const { mutate: editUser, isLoading: editLoading } = useEditUser();

    const status = [
        { 'label': "Active", "value": true },
        { 'label': "InActive", "value": false },
    ]

    const validateMessages = {
        required: 'Enter your email id!',
        types: {
            email: 'Enter valid email id!',
        }
    }

    const uploadProps = {
        name: 'file',
        multiple: false,
        accept: 'images/*',
        onChange(info) {
            const file = info.fileList[0];
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
            if (!isJpgOrPng) {
                return false
            }
            setFiles(info.fileList);
        },
        onDrop(e) {
            const file = e.dataTransfer.files[0];
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
            if (!isJpgOrPng) {
                return false
            }
            setFiles(e.dataTransfer.files)
        },
    };

    const beforeUpload = (file) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
        if (!isJpgOrPng) {
            message.error('You can only upload JPG/PNG file!');
            form.setFieldsValue({ "floorImage": null });
            setFiles(null);
        }
        return isJpgOrPng;
    };

    const onFinish = (values) => {
        if (create) {
            const formData = new FormData();
            if (files !== null) {
                formData.append("profileImage", files[0]?.originFileObj)
            }
            formData.append("firstName", values.firstName.trim())
            formData.append("lastName", values.lastName?.trim() || "")
            formData.append("email", values.email.trim())
            formData.append("password", values.password)
            formData.append("roleId", values.roleId)
            formData.append("status", true)
            addUser(formData, {
                onSuccess: (response) => {
                    if (response.statusCode === 200) {
                        message.success(response?.message);
                        navigate("/admin")
                    }
                    else {
                        message.error(response?.message);
                    }
                },
                onError: (error) => {
                    if (error?.response?.data?.errorMessages) {
                        const errorMessages = error.response.data.errorMessages;
                        setServerErrors(errorMessages);

                    }
                }
            });
        } else {
            const formData = new FormData();
            if (files !== null) {
                formData.append("profileImage", files[0]?.originFileObj)
            }
            formData.append("internalUserId", values.internalUserId)
            formData.append("firstName", values.firstName.trim())
            formData.append("lastName", values.lastName?.trim() || "")
            formData.append("email", values.email.trim())
            if (values.password !== undefined) {
                formData.append("password", values.password)
            }
            formData.append("roleId", values.roleId)
            if (editData.state.status !== values.status) formData.append("status", values.status)
            editUser(formData, {
                onSuccess: (response) => {
                    if (response.statusCode === 200) {
                        message.success(response?.message);
                        navigate("/admin")
                    }
                    else {
                        message.error(response?.message);
                    }
                },
                onError: (error) => {
                    if (error?.response?.data?.errorMessages) {
                        const errorMessages = error.response.data.errorMessages;
                        setServerErrors(errorMessages);

                    }
                }
            });
        }

    }

    const clearError = (name) => {
        setServerErrors(prev => ({ ...prev, [name]: "" }));
    };

    useEffect(() => {
        if (!create) {
            form.setFieldsValue({
                "internalUserId": editData?.state.internalUserId,
                "firstName": editData?.state.firstName,
                "lastName": editData?.state.lastName,
                "profileImage": editData?.state.profileImageUrl,
                "email": editData?.state.email,
                "roleId": editData?.state.roleId,
                "status": editData?.state.status,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editData]);


    return (
        <Fragment>
            <Form
                className="form-page"
                form={form}
                onFinish={onFinish}
                validateMessages={validateMessages}
                colon={false}
                layout="vertical">
                <div className="form-head form-head-sticky">
                    <div className="form-head-left">
                        <Link to="/admin" className="form-back">
                            <Image name="backArrow.svg" />
                        </Link>
                        <div className="form-title">
                            <h1>{create ? "New User" : "Edit User"}</h1>
                            <p>{create ? "Create" : "Edit"} user with roles &amp; responsibilities</p>
                        </div>
                    </div>
                    <div className="form-actions">
                        <Button className="form-cancel" onClick={() => navigate("/admin")}>Cancel</Button>
                        <Button className="form-submit" loading={Loading || editLoading} htmlType="submit">
                            <Image className="save-img" name="save.svg" /> Save
                        </Button>
                    </div>
                </div>

                <div className="form-card">
                    <Form.Item name="internalUserId" hidden={true}>
                        <Input />
                    </Form.Item>

                    <section className="form-section">
                        <div className="form-section-head">
                            <h2>User details</h2>
                            <p>Who this user is and how they sign in.</p>
                        </div>
                        <Row gutter={[24, 0]}>
                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: "Enter the first name!" }]}
                                    validateStatus={serverErrors?.firstName ? "error" : ""}
                                    help={serverErrors?.firstName?.[0]}>
                                    <Input placeholder="Enter your first name" maxLength={40} onChange={() => clearError("firstName")}></Input>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: "Enter the last name!" }]}
                                    validateStatus={serverErrors?.lastName ? "error" : ""}
                                    help={serverErrors?.lastName?.[0]}
                                >
                                    <Input placeholder="Enter your last name" maxLength={40} onChange={() => clearError("lastName")}></Input>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item label="Email" name="email" rules={[{ required: true, message: "Enter your email id!" }]}
                                    validateStatus={serverErrors?.email ? "error" : ""}
                                    help={serverErrors?.email?.[0]}
                                >
                                    <Input placeholder="Enter your email id" maxLength={40} onChange={() => clearError("email")}></Input>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item label="Role" name="roleId" rules={[{ required: true, message: "Select role" }]}>
                                    <Select placeholder="Select role">
                                        {userRoleData?.data.map((item, e) => (
                                            <Option key={e} value={item?.roleId}>
                                                {item?.roleName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            {create ? null : (
                                <Col xs={24} sm={12} lg={6}>
                                    <Form.Item label="Status" name="status" rules={[{ required: true, message: "Status is required" }]}>
                                        <Select placeholder={"Select status"} options={status} />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </section>

                    <section className="form-section">
                        <div className="form-section-head">
                            <h2>Password</h2>
                            <p>{create ? "Set the password this user signs in with." : "Leave untouched unless this user needs a new password."}</p>
                        </div>
                        <Row gutter={[24, 0]}>
                            {create ? null : (
                                <Col xs={24} md={12}>
                                    <Form.Item label=" ">
                                        <Button className="form-cancel" onClick={() => setPassword(!showPassword)}>
                                            {showPassword ? "Keep current password" : "Change Password"}
                                        </Button>
                                    </Form.Item>
                                </Col>
                            )}
                            <Col xs={24} md={12}>
                                <Form.Item label="Password" name="password" hidden={create ? false : showPassword ? false : true} rules={[{ required: create ? true : showPassword ? true : false, message: "Enter the password!" }, strongPasswordRule]}>
                                    <Input.Password placeholder="Enter the passowrd" maxLength={40}></Input.Password>
                                </Form.Item>
                            </Col>
                        </Row>
                    </section>

                    <section className="form-section">
                        <div className="form-section-head">
                            <h2>User image</h2>
                            <p>Shown next to this user across the app.</p>
                        </div>
                        <Form.Item label="" name="profileImage" rules={[{ message: "Profile image is required!" }]}>
                            <div className="form-upload">
                                <div className="form-upload-preview">
                                    {files === null ?
                                        editData.state !== null && editData?.state.profileImageUrl !== null ?
                                            <PrivateImage privateUrl={editData?.state.profileImageUrl} imgType={profile_image_type} className="profile-img" />
                                            : <Image className="profile-img" name="floorImage.png" />
                                        :
                                        <img
                                            src={URL.createObjectURL(files[0]?.originFileObj)}
                                            alt=".."
                                            className="profile-img"
                                        />}
                                </div>
                                <Upload {...uploadProps} showUploadList={false} maxCount={1} beforeUpload={beforeUpload}>
                                    <div className="form-upload-drop">
                                        <Image name='Gallery.svg' />
                                        Click to upload
                                        <p>PNG, JPG or GIF (max 400x400px)</p>
                                    </div>
                                </Upload>
                            </div>
                        </Form.Item>
                    </section>
                </div>
            </Form>
        </Fragment>
    )
}
export default FormAdmin;